import React, { createContext, useState, useEffect, useRef } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail 
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { enqueueSnackbar } from 'notistack';
import { auth, db } from '../../shared/utils/firebase';
import { clearLocalCache } from '../../shared/utils/firestoreCache';
import { flushPendingWork, hasPendingWork } from '../../shared/utils/pendingWork';
import { computeHasFeature } from './hasFeature';
import { managedLocations } from './managedLocations';

// Define the user role type
export type UserRole = 'USER' | 'MANAGER' | 'ADMIN';

// Define the user metadata type
export interface UserMetadata {
  uid: string;
  displayName: string;
  email: string;
  role: UserRole;
  coach_id?: string;
  assigned_location?: string;
  /** Every location a manager oversees, primary first; see managedLocations(). */
  assigned_locations?: string[];
  created_at: Date;
  updated_at: Date;
  photoURL?: string;
  feature_flags?: Record<string, boolean>;
}

// Define the auth context type
interface AuthContextType {
  user: User | null;
  userRole: UserRole | null;
  userMetadata: UserMetadata | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  register: (email: string, password: string, name: string, role: UserRole, coachId?: string, location?: string) => Promise<User>;
  resetPassword: (email: string) => Promise<void>;
  checkAccess: (resourceType: string, resourceData: any, operation: string) => boolean;
  hasFeature: (feature: string) => boolean;
}

// Create the auth context
export const AuthContext = createContext<AuthContextType>({
  user: null,
  userRole: null,
  userMetadata: null,
  loading: true,
  login: async () => { throw new Error('Not implemented'); },
  logout: async () => { throw new Error('Not implemented'); },
  register: async () => { throw new Error('Not implemented'); },
  resetPassword: async () => { throw new Error('Not implemented'); },
  checkAccess: () => false,
  hasFeature: () => false
});

// Helper function to get development role override
const getDevelopmentRoleOverride = (): UserRole | null => {
  const devUserRole = localStorage.getItem('dev_user_role');
  if (devUserRole === 'ADMIN' || devUserRole === 'MANAGER' || devUserRole === 'USER') {
    console.log(`Development mode: Using role override: ${devUserRole}`);
    return devUserRole as UserRole;
  }
  return null;
};

// Helper function to convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
};

// Create the auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userMetadata, setUserMetadata] = useState<UserMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const hadUserRef = useRef(false);

  // Handle auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const hadUser = hadUserRef.current;
      hadUserRef.current = Boolean(user);
      if (!user && hadUser) {
        // The session ended, in this tab or another. A logout elsewhere clears
        // the shared Firestore cache, which terminates this tab's Firestore
        // client too, so start from a clean page rather than a dead client.
        window.location.replace('/login');
        return;
      }
      setUser(user);
      
      if (user) {
        try {
          // Get user metadata from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            const userMetadata: UserMetadata = {
              uid: userData.uid,
              displayName: userData.displayName,
              email: userData.email,
              role: userData.role,
              coach_id: userData.coach_id,
              assigned_location: userData.assigned_location,
              assigned_locations: Array.isArray(userData.assigned_locations) ? userData.assigned_locations : undefined,
              created_at: convertTimestamp(userData.created_at),
              updated_at: convertTimestamp(userData.updated_at),
              photoURL: userData.photoURL || undefined,
              feature_flags: userData.feature_flags || undefined
            };
            
            // Check for development mode role override
            const devRole = getDevelopmentRoleOverride();
            setUserRole(devRole || userMetadata.role);
            setUserMetadata(userMetadata);
          } else {
            console.error('No user document found for logged in user');
            // Check for development mode role override even if no user document exists
            const devRole = getDevelopmentRoleOverride();
            if (devRole) {
              setUserRole(devRole);
              // Create a minimal user metadata object
              setUserMetadata({
                uid: user.uid,
                displayName: user.displayName || 'Developer',
                email: user.email || 'dev@example.com',
                role: devRole,
                created_at: new Date(),
                updated_at: new Date()
              });
            }
          }
        } catch (error) {
          console.error('Error fetching user metadata:', error);
          
          // If there's an error, check for development mode role override
          const devRole = getDevelopmentRoleOverride();
          if (devRole) {
            setUserRole(devRole);
            // Create a minimal user metadata object
            setUserMetadata({
              uid: user.uid,
              displayName: user.displayName || 'Developer',
              email: user.email || 'dev@example.com',
              role: devRole,
              created_at: new Date(),
              updated_at: new Date()
            });
          }
        }
      } else {
        // Reset user data when logged out
        setUserRole(null);
        setUserMetadata(null);
        // Clear development mode role override on logout
        localStorage.removeItem('dev_user_role');
      }
      
      setLoading(false);
    });

    // Clean up subscription
    return () => unsubscribe();
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<User> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'user_activity', userCredential.user.uid), {
        lastLogin: serverTimestamp(),
        userId: userCredential.user.uid,
        // Keep existing data using merge option
      }, { merge: true });
      // Fetch user role from Firestore
      let userRole = 'Unknown';
      try {
        const userDocSnap = await getDoc(doc(db, 'users', userCredential.user.uid));
        if (userDocSnap.exists()) {
          const userDocData = userDocSnap.data();
          if (userDocData && userDocData.role) {
            userRole = userDocData.role;
          }
        }
      } catch (err) {
        console.error('Error fetching user role for audit log:', err);
      }
      await setDoc(doc(db, 'audit_logs', `login_${Date.now()}`), {
        action: 'User Login',
        actionType: 'ACCESS',
        userId: userCredential.user.uid,
        userName: userCredential.user.displayName || email,
        userRole,
        timestamp: serverTimestamp(),
        details: 'User login successful'
      });
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      // Commit anything still inside an undo window (an Approve, an Archive)
      // while Firestore and the session are still alive.
      if (hasPendingWork()) {
        // notistack's global enqueue (the SnackbarProvider wraps this provider)
        // is unset while no provider is mounted, and must never stop sign-out.
        // The toast stays until the reload to /login replaces the page.
        enqueueSnackbar?.('Saving your last change before signing out…', { variant: 'info', persist: true });
      }
      await flushPendingWork();
      // Wipe the offline cache first so the next person on this device starts
      // clean (this terminates the Firestore client in every open tab), then
      // sign out so every tab's auth listener reloads itself.
      await clearLocalCache(db);
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Hard reload: the Firestore instance cannot be reused once terminated.
      window.location.replace('/login');
    }
  };

  // Register function
  const register = async (
    email: string, 
    password: string, 
    name: string, 
    role: UserRole, 
    coachId?: string, 
    location?: string
  ): Promise<User> => {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userData: Omit<UserMetadata, 'created_at' | 'updated_at'> & { 
        created_at: any, 
        updated_at: any 
      } = {
        uid: user.uid,
        displayName: name,
        email,
        role,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        photoURL: user.photoURL || undefined
      };
      
      // Add coach_id if user is a coach (USER role)
      if (role === 'USER' && coachId) {
        userData.coach_id = coachId;
      }
      
      // Add assigned_location if user is a manager
      if (role === 'MANAGER' && location) {
        userData.assigned_location = location;
      }
      
      await setDoc(userDocRef, userData);
      
      // Create audit log for user creation
      const auditLogRef = doc(db, 'audit_logs', `user_create_${user.uid}_${Date.now()}`);
      await setDoc(auditLogRef, {
        action: 'user_create',
        user_id: user.uid,
        role,
        performed_by: 'system', // Updated to the actual user who created the account in a real scenario
        timestamp: serverTimestamp()
      });
      
      return user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Reset password function
  const resetPassword = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  // Check access function
  const checkAccess = (resourceType: string, resourceData: any, operation: string): boolean => {
    if (!userRole || !userMetadata) return false;
    const inManagedLocation = (data: any) => managedLocations(userMetadata).includes(data?.primary_location);

    // Admin has full access
    if (userRole === 'ADMIN') return true;

    // Handle client data access
    if (resourceType === 'client') {
      // Manager can access clients in their locations
      if (userRole === 'MANAGER') {
        if (operation === 'read' || operation === 'update' || operation === 'create') {
          return inManagedLocation(resourceData);
        }
        
        // Managers cannot delete clients
        return false;
      }
      // Coach can only access their assigned clients
      if (userRole === 'USER') {
        // Make sure coach_id exists before using it
        if (!userMetadata.coach_id) return false;
        
        if (operation === 'read' || operation === 'update') {
          return resourceData.lead_coach_id?.toString() === userMetadata.coach_id.toString() || 
                 (resourceData.collaborating_coach_ids && 
                  resourceData.collaborating_coach_ids.includes(userMetadata.coach_id));
        }
        
        // Coaches cannot create or delete clients
        return false;
      }
    }
    
    // Handle check-in data access
    if (resourceType === 'checkin') {
      // Managers can access and modify check-ins for their locations
      if (userRole === 'MANAGER') {
        return inManagedLocation(resourceData);
      }
      
      // Coaches can only view check-in completion status, not the data itself
      if (userRole === 'USER' && operation === 'read_status') {
        if (!userMetadata.coach_id) return false;
        return resourceData.lead_coach_id?.toString() === userMetadata.coach_id.toString() || 
              (resourceData.collaborating_coach_ids && 
               resourceData.collaborating_coach_ids.includes(userMetadata.coach_id));
      }
      
      // Coaches cannot create, update, delete, or read check-in data
      return false;
    }
    
    // Handle NPS survey data access
    if (resourceType === 'nps') {
      // Managers can view NPS data for their locations
      if (userRole === 'MANAGER' && operation === 'read') {
        return inManagedLocation(resourceData);
      }
      
      // Coaches can only view NPS completion status, not the data itself
      if (userRole === 'USER' && operation === 'read_status') {
        if (!userMetadata.coach_id) return false;
        return resourceData.lead_coach_id?.toString() === userMetadata.coach_id.toString() || 
              (resourceData.collaborating_coach_ids && 
               resourceData.collaborating_coach_ids.includes(userMetadata.coach_id));
      }
    }
    
    // By default, deny access
    return false;
  };

  // Feature-flag check (per-user, admin-managed via feature_flags map)
  const hasFeature = (feature: string): boolean => {
    return computeHasFeature(userMetadata, feature);
  };

  // Provide the auth context values
  const value = {
    user,
    userRole,
    userMetadata,
    loading,
    login,
    logout,
    register,
    resetPassword,
    checkAccess,
    hasFeature
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
